import type { ImportMethod } from '../db/schema';
import { candidateImages } from './images';
import { pageText } from './html';
import { productFromJsonLd } from './jsonld';
import { hasOpenGraph, openGraph } from './og';
import { readSite } from './sites';
import type { Extracted } from './product';

/**
 * Structured data first, always. A site adapter if one knows this shop, then
 * JSON-LD, then OpenGraph, then the bare page.
 *
 * This is the one seam in the pipeline: everything upstream produces a
 * `Document`, everything downstream consumes an `Extracted`, and a cleanup
 * pass over the description — the thing stage 4 originally specced an API key
 * for — would slot in here without either side noticing.
 *
 * Fields fall through independently. A page can carry a Product block with no
 * description and still have a perfectly good `og:description`; taking the
 * whole record from one source would throw that away.
 */
export function extract(html: string, finalUrl: string): Extracted {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const site = readSite(doc, finalUrl);
  const structured = productFromJsonLd(doc);
  const og = openGraph(doc);
  const page = pageText(doc, finalUrl);

  const title = site?.title ?? structured?.title ?? og.title ?? page.title;

  // An adapter that named the gallery outranks any heuristic over `<img>`
  // tags — it knows which pictures are the product.
  const seeds = [...(structured?.images ?? []), ...og.images];
  const images =
    site?.images !== undefined && site.images.length > 0
      ? site.images
      : candidateImages(doc, finalUrl, seeds);

  return {
    title,
    description: describe(
      [site?.description, structured?.description, og.description, page.description],
      // Both spellings of the name: `rawTitle` still carries the "| Shop"
      // suffix that `title` has had stripped, and shops set their meta
      // description to either one.
      [title, page.rawTitle],
    ),
    price: site?.price ?? structured?.price ?? og.price,
    currency: site?.currency ?? structured?.currency ?? og.currency,
    images,
    method: methodOf(site !== undefined, structured !== undefined, hasOpenGraph(og)),
  };
}

/**
 * The first candidate that says something the title didn't already.
 *
 * Plenty of shops set `meta[name=description]` to the page title verbatim —
 * Amazon's is byte-identical to `<title>` — and storing that as the store's
 * description means the panel shows the same sentence twice.
 *
 * A description that merely *begins* with the product name is fine and common
 * (IKEA's does), so the test is length: only something barely longer than the
 * title it contains is a restatement rather than a description.
 */
function describe(
  candidates: readonly (string | undefined)[],
  names: readonly (string | undefined)[],
): string {
  for (const candidate of candidates) {
    const text = candidate?.trim();
    if (text === undefined || text === '') continue;

    if (names.some((name) => name !== undefined && restates(text, name))) continue;
    return text;
  }

  return '';
}

function restates(description: string, title: string): boolean {
  const a = squash(description);
  const b = squash(title);
  if (b === '') return false;

  return a === b || (a.includes(b) && a.length <= b.length * 1.15);
}

function squash(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Provenance names the best source that contributed, not the one that happened
 * to supply the title — it's there to answer "how much should I trust this
 * text" months from now.
 */
function methodOf(hasSite: boolean, hasJsonLd: boolean, hasOg: boolean): ImportMethod {
  if (hasSite) return 'site';
  if (hasJsonLd) return 'jsonld';
  if (hasOg) return 'og';
  return 'html';
}
